/**
 * Meta Conversion API 이벤트 전송 유틸리티
 *
 * /v2/meta/capi/events 엔드포인트를 호출하여 서버사이드 이벤트를 전송한다.
 * IP/UA/GeoIP는 백엔드에서 자동 보강되므로 프론트에서는 PII + fbp/fbc만 전송.
 */
import * as Sentry from '@sentry/react'

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://api-dev.1bpluschool.com'

// v1 `/meta/events` 는 2026-04-13 에 제거됐다 (a627b4d1).
const CAPI_EVENTS_PATH = '/v2/meta/capi/events'

export async function sendCapiEvent(params: {
  mallId: string
  shopNo: number | string
  eventName: string       // 'Lead', 'ViewContent' 등
  eventId: string         // crypto.randomUUID()
  eventSourceUrl: string  // window.location.href
  userData: Record<string, string | undefined>  // 해싱된 PII + fbp/fbc
  customData: Record<string, unknown>           // content_ids 등
}): Promise<void> {
  // fire-and-forget: 실패는 기록만 하고 신청 흐름을 막지 않는다
  try {
    const response = await fetch(`${API_BASE_URL}${CAPI_EVENTS_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mall_id: params.mallId,
        shop_no: Number(params.shopNo),
        event_name: params.eventName,
        event_id: params.eventId,
        event_time: Math.floor(Date.now() / 1000),
        event_source_url: params.eventSourceUrl,
        user_data: params.userData,
        custom_data: params.customData,
      }),
    })

    // 응답을 아예 안 보던 탓에 v1 제거 후의 404 가 4개월간 무증상이었다
    // (2026-04-13 ~ 08-12). 잃은 것은 **서버 레그뿐**이다 — 두 호출부 모두
    // trackPixelEvent 와 같은 블록에 있어 브라우저 픽셀 Lead 는 계속 발화했다.
    // 그래도 해싱 PII 로 얻는 EMQ, dedup 파트너, 픽셀 차단 세션 커버리지가
    // 통째로 빠졌다. 호출부가 이 Promise 를 await 하지 않으므로, 응답을 봐도
    // 신청 흐름은 블로킹되지 않는다.
    //
    // 규칙은 하나다: **응답이 성공 상태가 아니면 남긴다**. 상태 코드별로 다르게
    // 다루거나 본문을 읽어 서버의 skip 사유까지 보는 안을 검토했지만, 판단 축을
    // 더할 때마다 새 결함이 나와(리뷰 3라운드, 마지막 지적 6건 중 5건이 직전 수정
    // 산물) 단일 규칙으로 되돌렸다. 그래서 이 규칙은 "전송 성공"이 아니라 "응답
    // 상태"까지만 본다 — 아래 ⚠️ 가 그 간극이다. 연속 중복은 Sentry 기본 dedupe 가
    // 눌러준다(실측: 같은 404 를 2회 보내면 1건만 전송).
    if (!response.ok) {
      const message = `Meta CAPI 이벤트 전송 실패 (HTTP ${response.status})`
      console.warn(message, {
        path: CAPI_EVENTS_PATH,
        eventName: params.eventName,
      })
      Sentry.captureMessage(message, {
        level: 'warning',
        tags: {
          capi_status: String(response.status),
          capi_event: params.eventName,
        },
        extra: {
          path: CAPI_EVENTS_PATH,
          mallId: params.mallId,
          shopNo: params.shopNo,
        },
      })
    }
    // ⚠️ 알려진 한계: HTTP 200 인데 서버가 전송을 건너뛴 경우(샵 CAPI 미연동,
    // 회원 전용 모드 — 우리는 member_id 를 보내지 않는다, event_time 이 Meta 7일
    // 창 밖)는 여기서 안 잡힌다. 몰 설정 문제라 대시보드 쪽에서 드러내는 편이 맞다.
  } catch (err) {
    // 응답이 아예 없는 실패 — API 호스트·TLS 변경 같은 전면 장애와 payload 직렬화
    // 오류가 여기로 온다. 둘 다 유실이라 구분 없이 남긴다.
    //
    // 광고차단기로 이 fetch 가 막힌 세션은 여기서 관측되지 않는다. 같은 차단
    // 목록이 Sentry ingest 도 막아 captureMessage 가 함께 버려지기 때문이다.
    // 그쪽 규모를 알려면 서버측 카운터가 필요하다.
    //
    // VITE_API_BASE_URL 이 비어 위의 dev 폴백으로 새는 사고도 여기서 안 잡힌다 —
    // api-dev 는 살아 있는 호스트라 reject 가 아니라 200 이 오고, dev 는 같은
    // dev Cloud SQL 을 보므로 양쪽에 있는 몰이면 아무 신호도 남지 않는다.
    // 그건 빌드 타임에 env 를 단언해야 잡힌다 (현재는 .env.production 이 채운다).
    const message = 'Meta CAPI 이벤트 전송 실패 (응답 없음)'
    console.warn(message, err)
    Sentry.captureMessage(message, {
      level: 'warning',
      tags: { capi_status: 'no_response', capi_event: params.eventName },
      extra: {
        path: CAPI_EVENTS_PATH,
        apiBaseUrl: API_BASE_URL,
        mallId: params.mallId,
        // 값이 아니라 종류만 남는다 (예: 'TypeError: Failed to fetch').
        error: String(err),
      },
    })
  }
}
